/**
 * GlobalFiasAIWindow — THE Fias AI window, mounted once by
 * GlobalFiasAIProvider (above <Routes>) so it floats over every page and
 * survives navigation.
 *
 * Desktop: a movable, resizable floating window (drag by header via the
 * shared useDraggable; resize by the corner handle) with NO backdrop —
 * the user can work in the app alongside it. Geometry persists to
 * localStorage. Mobile (< lg): a full-screen panel — a tiny draggable
 * desktop window on a phone is an anti-pattern.
 *
 * Escape closes. The scope chip shows which arche the assistant is
 * currently acting in ("Platform" outside arches).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  User,
  X,
  Minus,
  SquarePen,
  Loader2,
  Ghost,
  Settings,
  Terminal,
  History,
  CalendarClock,
  MessageSquare,
  MoreHorizontal,
  Check,
  Flag,
  type LucideIcon,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { NotificationBadge } from '@/components/ui/notification-badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { HolderSwitcherChip } from '@/components/HolderSwitcher';
import { useDraggable } from '@/lib/hooks/useDraggable';
import { FiasAIInput } from './FiasAIInput';
import { FiasAIAgentEvents } from './FiasAIAgentEvents';
import { FiasAIConfirmationDialog } from './FiasAIConfirmationDialog';
import { useGlobalFiasAI } from './hooks/GlobalFiasAIContext';
import { FiasAIVerbosePanel } from './FiasAIVerbosePanel';
import { FiasAIArcheSuggestions, deriveArcheSuggestions } from './FiasAIArcheSuggestions';
import { FiasAIHistoryPane, FiasAISchedulesPane, FiasAIOptionsPane } from './FiasAIWindowPanes';
import { FiasAiReportProblemDialog } from './FiasAiReportProblemDialog';
import { useAuth } from '@/auth/hooks/useAuth';

const GEOMETRY_KEY = 'fias-ai-window-geometry';
const MIN_W = 460;
const MIN_H = 420;
const DEFAULT_W = 560;
const DEFAULT_H = 640;

// Below this window width the header's action toggles collapse into the "More"
// menu; at or above it they show inline. Driven in JS (measured window width)
// rather than a CSS container query because `container-type` on the header
// broke the Radix dropdowns (workspace switcher + More menu wouldn't open).
const COMPACT_HEADER_WIDTH = 600;

/** True when the viewport is at the desktop floating-window breakpoint (md+). */
function matchesDesktopViewport(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(min-width: 768px)').matches;
}

interface Geometry {
  width: number;
  height: number;
}

function loadGeometry(): Geometry {
  try {
    const raw = localStorage.getItem(GEOMETRY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Geometry;
      if (parsed.width >= MIN_W && parsed.height >= MIN_H) return parsed;
    }
  } catch {
    // fall through to defaults
  }
  return { width: DEFAULT_W, height: DEFAULT_H };
}

// --- Minimized floating button ---------------------------------------------

const FAB_POS_KEY = 'fias-ai-fab-position';
const FAB_SIZE = 56;

interface FabPos {
  right: number;
  bottom: number;
}

function loadFabPos(): FabPos {
  try {
    const raw = localStorage.getItem(FAB_POS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as FabPos;
      if (typeof p.right === 'number' && typeof p.bottom === 'number') return p;
    }
  } catch {
    // fall through to default
  }
  return { right: 24, bottom: 24 };
}

/**
 * The collapsed Fias AI button — a draggable, clickable puck in the corner.
 * A tap (no drag past the threshold) reopens the window; a drag repositions
 * it (persisted). It's the only reopen affordance on pages with no header
 * (minimal-header arche shells, signed-out routes), so it must be movable.
 */
function MinimizedFab({ onOpen }: { onOpen: () => void }) {
  const [pos, setPos] = useState<FabPos>(loadFabPos);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    baseRight: number;
    baseBottom: number;
    moved: boolean;
    lastPos: FabPos;
  } | null>(null);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        baseRight: pos.right,
        baseBottom: pos.bottom,
        moved: false,
        lastPos: pos,
      };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [pos],
  );

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const s = dragRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.moved && Math.hypot(dx, dy) > 4) s.moved = true; // click-vs-drag threshold
    if (s.moved) {
      // Anchored bottom-right: dragging right/down shrinks right/bottom.
      const next: FabPos = {
        right: Math.max(8, Math.min(window.innerWidth - FAB_SIZE, s.baseRight - dx)),
        bottom: Math.max(8, Math.min(window.innerHeight - FAB_SIZE, s.baseBottom - dy)),
      };
      s.lastPos = next;
      setPos(next);
    }
  }, []);

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const s = dragRef.current;
      if (!s || s.pointerId !== e.pointerId) return;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      dragRef.current = null;
      if (s.moved) {
        try {
          localStorage.setItem(FAB_POS_KEY, JSON.stringify(s.lastPos));
        } catch {
          // non-fatal — position just won't persist
        }
      } else {
        onOpen(); // a tap (no drag) reopens the window
      }
    },
    [onOpen],
  );

  return (
    <button
      type="button"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      className="fixed z-50 flex h-14 w-14 touch-none select-none items-center justify-center rounded-full border-[3px] border-primary/70 bg-background shadow-2xl transition-colors hover:bg-muted"
      style={{ right: pos.right, bottom: pos.bottom }}
      title="Open Fias AI"
      aria-label="Open Fias AI"
      data-testid="fias-ai-minimized-fab"
    >
      {/* draggable=false + pointer-events-none so pressing the logo never
          starts a native image drag (which would fire pointercancel and be
          mistaken for a tap, reopening the window instead of moving it). */}
      <img
        src="/Heptagen_Logo_1.png"
        alt="Fias AI"
        className="pointer-events-none h-7 w-7"
        draggable={false}
      />
    </button>
  );
}

export function GlobalFiasAIWindow() {
  const ai = useGlobalFiasAI();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('admin') ?? false;
  const [view, setView] = useState<'chat' | 'history' | 'schedules' | 'options'>('chat');
  const [reportOpen, setReportOpen] = useState(false);
  const drag = useDraggable();
  const [geometry, setGeometry] = useState<Geometry>(loadGeometry);
  const resizeRef = useRef<{
    startX: number;
    startY: number;
    baseW: number;
    baseH: number;
    pointerId: number;
  } | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isDesktopViewport, setIsDesktopViewport] = useState(matchesDesktopViewport);

  // The approval window auto-opens when a confirmation arrives and closes when
  // it settles. Dismissing the window (esc / X) leaves the pending in place —
  // the inline card's "Review & approve" reopens it — so an accidental dismiss
  // never rejects.
  const pendingConfirmationId = ai.pendingConfirmation?.interactionId ?? null;
  const [reviewOpenId, setReviewOpenId] = useState<string | null>(null);
  useEffect(() => {
    setReviewOpenId(pendingConfirmationId);
  }, [pendingConfirmationId]);

  // Track the desktop/mobile breakpoint so header compaction uses the real
  // width: the floating window's geometry on desktop, always-compact on the
  // mobile full-screen panel.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsDesktopViewport(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(GEOMETRY_KEY, JSON.stringify(geometry));
    } catch {
      // best-effort persistence
    }
  }, [geometry]);

  // Escape closes (only while open).
  useEffect(() => {
    if (!ai.isPanelOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') ai.closePanel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [ai.isPanelOpen, ai.closePanel, ai]);

  // Autoscroll on new content.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ai.messages]);

  const onResizeDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      resizeRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        baseW: geometry.width,
        baseH: geometry.height,
        pointerId: e.pointerId,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [geometry],
  );
  const onResizeMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const s = resizeRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    // The window is anchored right/bottom, so its bottom-right corner is
    // pinned and the TOP-LEFT is the corner that moves when it grows.
    // The handle lives there, and the deltas are inverted so the grabbed
    // corner follows the pointer (drag up-left = grow).
    setGeometry({
      width: Math.min(Math.max(s.baseW - (e.clientX - s.startX), MIN_W), window.innerWidth - 24),
      height: Math.min(Math.max(s.baseH - (e.clientY - s.startY), MIN_H), window.innerHeight - 24),
    });
  }, []);
  const onResizeUp = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const s = resizeRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    resizeRef.current = null;
  }, []);

  // Collapsed: render only the draggable corner button (chat state lives in
  // the provider, so nothing is lost while minimized).
  if (ai.isMinimized) return <MinimizedFab onOpen={ai.openPanel} />;
  if (!ai.isPanelOpen) return null;

  // Scope chip shows where the user is. Prefer the current arche from the route
  // — correct for every arche, including ones that don't register AI
  // capabilities — then the AI-registered arche, then Platform. (activeArcheId
  // alone was only set by arches that call useRegisterArcheAI, so most arches
  // wrongly showed "Platform".)
  const routeArcheId = location.pathname.match(/^\/a\/([^/]+)/)?.[1] ?? null;
  const currentArcheId = routeArcheId ?? ai.activeArcheId;
  const scopeLabel = currentArcheId
    ? currentArcheId.replace(/^arche_/, '').replace(/_/g, ' ')
    : 'Platform';

  // Collapse the action toggles into the More menu only when the window is
  // genuinely narrow (or on the mobile full-screen panel).
  const compact = !isDesktopViewport || geometry.width < COMPACT_HEADER_WIDTH;

  const busyPlaceholder =
    ai.busyState === 'awaiting_user'
      ? "Answer Fias's question…"
      : ai.busyState === 'awaiting_confirmation'
        ? 'Review the confirmation card above, or type to change course…'
        : ai.busyState === 'thinking'
          ? 'Fias is working…'
          : 'Ask Fias AI anything…';

  // Secondary header actions (view toggles + settings). Rendered inline when
  // the window is wide and collapsed into the "More" menu when it's narrow, so
  // they never crowd the workspace chip. One source of truth for both renders.
  const secondaryActions: Array<{
    key: string;
    label: string;
    title: string;
    Icon: LucideIcon;
    active: boolean;
    activeClass?: string;
    onClick: () => void;
    testid: string;
    badge?: number;
  }> = [
    {
      key: 'history',
      label: 'History',
      title: 'Conversation history (shared with Make a Request)',
      Icon: History,
      active: view === 'history',
      onClick: () => setView(view === 'history' ? 'chat' : 'history'),
      testid: 'fias-ai-history-toggle',
    },
    {
      key: 'schedules',
      label: 'Scheduled tasks',
      title: 'Scheduled AI tasks',
      Icon: CalendarClock,
      active: view === 'schedules',
      onClick: () => setView(view === 'schedules' ? 'chat' : 'schedules'),
      testid: 'fias-ai-schedules-toggle',
      badge: ai.scheduledAlertCount,
    },
    {
      key: 'temporary',
      label: 'Temporary chat',
      title: ai.temporaryChat
        ? 'Temporary chat ON — memory is neither read nor updated'
        : 'Start temporary chat (no memory reads or writes)',
      Icon: Ghost,
      active: ai.temporaryChat,
      activeClass: 'text-amber-600',
      onClick: () => ai.setTemporaryChat(!ai.temporaryChat),
      testid: 'fias-ai-temporary-toggle',
    },
    ...(isAdmin
      ? [
          {
            key: 'verbose',
            label: 'Verbose logging',
            title: ai.verboseMode
              ? 'Verbose ON — streaming prompts, tools, and full tool IO (admin)'
              : 'Verbose debug mode (admin): stream prompts, tools, full tool IO',
            Icon: Terminal,
            active: ai.verboseMode,
            activeClass: 'text-green-600',
            onClick: () => ai.setVerboseMode(!ai.verboseMode),
            testid: 'fias-ai-verbose-toggle',
          },
        ]
      : []),
    {
      key: 'options',
      label: 'Options',
      title: 'Fias AI options',
      Icon: Settings,
      active: view === 'options',
      onClick: () => setView(view === 'options' ? 'chat' : 'options'),
      testid: 'fias-ai-options-toggle',
    },
    {
      key: 'report',
      label: 'Report a problem',
      title: 'Report a problem with Fias AI',
      Icon: Flag,
      active: false,
      onClick: () => setReportOpen(true),
      testid: 'fias-ai-report',
    },
  ];

  const windowBody = (
    <>
      {/* Header (drag handle on desktop). @container drives the responsive
          collapse of the action toggles into the More menu. */}
      <div
        className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border bg-linear-to-b from-muted/40 to-transparent flex-shrink-0 select-none"
        data-testid="fias-ai-window-header"
      >
        {/* Identity + acting workspace + scope. Takes the remaining width and
            truncates rather than colliding with the actions when narrow. */}
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          {/* Drag handle — ONLY the logo + title start a window drag. Keeping
              the drag handlers off the header (and off the workspace switcher /
              scope chip) is deliberate: those are Radix dropdowns that open on
              pointerdown, and a drag handler on an ancestor swallowed the open. */}
          <div
            className="flex shrink-0 items-center gap-2 md:cursor-grab md:active:cursor-grabbing"
            {...drag.handlers}
          >
            {/* ml-3 clears the top-left resize handle (24px, 28px on hover);
                draggable=false + pointer-events-none so grabbing the logo drags
                the window rather than starting a native image drag. */}
            <img
              src="/Heptagen_Logo_1.png"
              alt="Fias"
              className="pointer-events-none ml-3 h-6 w-6 shrink-0"
              draggable={false}
            />
            <span className="shrink-0 text-sm font-semibold tracking-tight">Fias AI</span>
          </div>
          {/* Workspace the assistant acts in — mirrors the global header
              switcher (one source of truth). Hidden for personal-only users
              (nothing to switch). Switching starts a fresh chat because the
              session's acting Holder is pinned on the first turn. */}
          <HolderSwitcherChip compact />
          {/* Scope indicator + the whitespace up to the action buttons is also
              a drag handle (flex-1 fills that gap). The workspace switcher above
              is deliberately left OUT of it — a drag handler on an ancestor of
              its Radix trigger swallows the dropdown's open. */}
          <div
            className="flex min-w-0 flex-1 items-center gap-2 self-stretch md:cursor-grab md:active:cursor-grabbing"
            {...drag.handlers}
          >
            <span
              className="line-clamp-2 min-w-0 max-w-[8rem] rounded-md bg-muted px-2 py-0.5 text-center text-[10px] font-medium uppercase leading-tight tracking-wide text-muted-foreground"
              data-testid="fias-ai-scope-chip"
            >
              {scopeLabel}
            </span>
            {ai.temporaryChat && (
              <span
                className="shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400"
                data-testid="fias-ai-temporary-chip"
              >
                Temporary
              </span>
            )}
          </div>
        </div>

        {/* Actions. New chat + window controls stay inline; the view/setting
            toggles collapse into the More menu on a narrow window and expand
            back inline when it's wide. The inline group stays mounted (CSS
            hidden) when narrow so it remains a stable target. */}
        <div className="flex shrink-0 items-center gap-0.5">
          {ai.messages.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg"
              onClick={ai.reset}
              title="New chat"
              disabled={ai.isStreaming}
              data-testid="fias-ai-new-chat"
            >
              <SquarePen className="h-4 w-4" />
            </Button>
          )}

          <div className={`${compact ? 'hidden' : 'flex'} items-center gap-0.5`}>
            {secondaryActions.map((a) => (
              <Button
                key={a.key}
                variant="ghost"
                size="icon"
                className={`relative h-8 w-8 rounded-lg ${a.active ? (a.activeClass ?? 'text-primary') : ''}`}
                onClick={a.onClick}
                title={a.title}
                data-testid={a.testid}
              >
                <a.Icon className="h-4 w-4" />
                {a.badge ? (
                  <NotificationBadge
                    count={a.badge}
                    className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 text-[9px] ring-2 ring-background"
                  />
                ) : null}
              </Button>
            ))}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={`relative h-8 w-8 rounded-lg ${compact ? '' : 'hidden'}`}
                title="More"
                aria-label="More options"
                data-testid="fias-ai-more"
              >
                <MoreHorizontal className="h-4 w-4" />
                <NotificationBadge
                  count={ai.scheduledAlertCount}
                  className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 text-[9px] ring-2 ring-background"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {secondaryActions.map((a) => (
                <DropdownMenuItem
                  key={a.key}
                  onSelect={() => a.onClick()}
                  className="flex items-center gap-2"
                >
                  <a.Icon
                    className={`h-4 w-4 shrink-0 ${a.active ? (a.activeClass ?? 'text-primary') : 'text-muted-foreground'}`}
                  />
                  <span className="flex-1">{a.label}</span>
                  {a.badge ? (
                    <NotificationBadge count={a.badge} />
                  ) : a.active ? (
                    <Check className="h-4 w-4 text-primary" />
                  ) : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Window controls, set off by a hairline. */}
          <div className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-lg"
            onClick={ai.minimizePanel}
            title="Minimize"
            aria-label="Minimize"
            data-testid="fias-ai-minimize"
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-lg hover:bg-destructive/10 hover:text-destructive"
            onClick={ai.closePanel}
            title="Close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {view === 'history' && (
        <FiasAIHistoryPane
          onSelect={(convId) => {
            void ai.loadConversation(convId);
            setView('chat');
          }}
          onClose={() => setView('chat')}
        />
      )}
      {view === 'schedules' && <FiasAISchedulesPane onClose={() => setView('chat')} />}
      {view === 'options' && (
        <FiasAIOptionsPane
          temporaryChat={ai.temporaryChat}
          setTemporaryChat={ai.setTemporaryChat}
          verboseMode={ai.verboseMode}
          setVerboseMode={ai.setVerboseMode}
          scheduledAlertsEnabled={ai.scheduledAlertsEnabled}
          setScheduledAlertsEnabled={ai.setScheduledAlertsEnabled}
          isAdmin={isAdmin}
          onClose={() => setView('chat')}
        />
      )}

      {/* Conversation */}
      <div
        className={`flex-1 overflow-y-auto px-4 py-3 space-y-4 ${view !== 'chat' ? 'hidden' : ''}`}
      >
        {ai.messages.length === 0 && (
          <div className="text-sm text-muted-foreground text-center pt-8">
            Ask a question, or ask Fias to do something — it can read and update your data such as
            files, contacts, calendar, messages, email, etc. and drive the arches that you are
            using.
          </div>
        )}
        {ai.messages.map((msg) => (
          <div key={msg.id} className={msg.role === 'user' ? 'flex justify-end' : ''}>
            {msg.role === 'user' ? (
              <div className="flex items-start gap-2 max-w-[85%]">
                <div className="rounded-2xl rounded-tr-sm bg-primary text-primary-foreground px-3 py-2 text-sm whitespace-pre-wrap break-words">
                  {msg.content}
                </div>
                <User className="h-4 w-4 mt-2 text-muted-foreground flex-shrink-0" />
              </div>
            ) : (
              <div className="max-w-[95%]">
                {msg.events && msg.events.length > 0 && (
                  <FiasAIAgentEvents
                    events={msg.events}
                    onRespondConfirmation={(id, approved, feedback) => {
                      if (ai.pendingConfirmation?.interactionId === id) {
                        void ai.respondToConfirmation(approved, feedback);
                      }
                    }}
                    onReviewConfirmation={(id) => setReviewOpenId(id)}
                    onAnswerQuestion={(answer) => void ai.submit(answer)}
                    pendingConfirmationId={ai.pendingConfirmation?.interactionId ?? null}
                    pendingQuestionId={ai.pendingQuestion?.interactionId ?? null}
                    onOpenRef={ai.openHandoff}
                  />
                )}
                {msg.content ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>
                ) : msg.isStreaming ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Working…
                  </div>
                ) : null}
                {!msg.isStreaming && (
                  <FiasAIArcheSuggestions
                    suggestions={deriveArcheSuggestions(msg)}
                    onOpen={(archeId) => navigate(`/a/${archeId}`)}
                  />
                )}
              </div>
            )}
          </div>
        ))}
        {isAdmin && ai.verboseMode && <FiasAIVerbosePanel events={ai.verboseEvents} />}
        <div ref={bottomRef} />
      </div>

      {/* Input — stays enabled while a question is pending (typed answers). */}
      {view === 'chat' ? (
        <FiasAIInput
          onSubmit={(text) => void ai.submit(text)}
          disabled={ai.busyState === 'thinking'}
          placeholder={busyPlaceholder}
          autoFocus
          draftKey="global"
          onStop={ai.busyState === 'thinking' ? () => void ai.stopTurn() : undefined}
        />
      ) : (
        <button
          type="button"
          onClick={() => setView('chat')}
          className="flex items-center justify-center gap-1.5 border-t border-border py-2 text-sm text-primary hover:bg-muted/50"
        >
          <MessageSquare className="h-3.5 w-3.5" /> Back to chat
        </button>
      )}
    </>
  );

  return (
    <>
      {/* Mobile: full-screen panel */}
      <div className="md:hidden fixed inset-0 z-50 flex flex-col bg-background">{windowBody}</div>

      {/* Desktop: floating, draggable, resizable window (no backdrop) */}
      <div
        className="hidden md:flex fixed z-50 flex-col rounded-2xl border-[3px] border-primary/70 bg-background shadow-2xl"
        style={{
          right: 24,
          bottom: 24,
          width: geometry.width,
          height: geometry.height,
          transform: `translate(${drag.pos.x}px, ${drag.pos.y}px)`,
        }}
        data-testid="fias-ai-window"
      >
        {windowBody}
        {/* Resize handle — TOP-LEFT: the corner that actually moves on a
            right/bottom-anchored window. Rendered as a solid corner tab in
            the window's border color (not a faint glyph) so the affordance
            is discoverable; brightens and grows slightly on hover. */}
        <div
          className="group absolute -top-[3px] -left-[3px] flex h-6 w-6 items-center justify-center rounded-tl-2xl rounded-br-xl bg-primary/70 transition-all hover:h-7 hover:w-7 hover:bg-primary cursor-nwse-resize"
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          onPointerCancel={onResizeUp}
          title="Drag to resize"
          aria-label="Drag to resize"
          data-testid="fias-ai-resize-handle"
        >
          <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 text-primary-foreground">
            <path
              d="M3 9L9 3M3 5.5L5.5 3M6.5 12L12 6.5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </div>
      </div>

      {/* Report-a-problem dialog. Mounted once (Radix portals it), driven by the
          "Report a problem" header/More-menu action. Snapshots the current
          conversation + model so support can diagnose the offending turn. */}
      <FiasAiReportProblemDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        conversationId={ai.conversationId}
        messages={ai.messages}
        modelInfo={ai.currentModel}
      />

      {/* The explicit-approval window — auto-opens on a pending confirmation.
          Dismissing it does NOT reject (the inline card reopens it). */}
      <FiasAIConfirmationDialog
        pending={ai.pendingConfirmation}
        open={pendingConfirmationId !== null && reviewOpenId === pendingConfirmationId}
        onOpenChange={(open) => {
          if (!open) setReviewOpenId(null);
        }}
        onApprove={(edits) => void ai.respondToConfirmation(true, undefined, edits)}
        onReject={(feedback) => void ai.respondToConfirmation(false, feedback)}
      />
    </>
  );
}
